import { type DependencyList, useCallback, useEffect, useRef, useState } from 'react';

import { useQueryParams } from '../routing/hooks';
import type { QueryParams } from '../routing/types';

/**
 * Wrap promise in hook. Returns result of promise along with loading and error state.
 *
 * Also provides functions for updating/replacing and refetching data.
 */
export function usePromise<T>(promise: Promise<T> | (() => Promise<T>)) {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState<Error | undefined>(undefined);
  const [data, setData] = useState<T | undefined>(undefined);

  const fetchFn = useRef<() => Promise<T | undefined>>();

  // Run fetch function as soon as `promise` changes
  useEffect(() => {
    fetchFn.current = async () => {
      try {
        setIsLoading(true);

        const res = await (typeof promise === 'function' ? promise() : promise);
        setData(res);
        return res;
      } catch (err: unknown) {
        setHasError(err instanceof Error ? err : new Error(JSON.stringify(err)));
      } finally {
        setIsLoading(false);
      }
    };

    fetchFn.current();
  }, [promise]);

  return {
    isLoading,
    hasError,
    data,
    setData,
    refetch: async () => fetchFn.current?.(),
  };
}

export function useAbortController() {
  const { current: abortController } = useRef(new AbortController());
  useEffect(() => {
    // Abort anything when component unmounts
    return () => {
      abortController.abort();
    };
  }, []);
  return abortController;
}

export function useIntersectionObserver({
  root,
  target,
  onIntersect,
  threshold = 1.0,
  rootMargin = '0px',
  enabled = true,
}: {
  root: React.RefObject<HTMLElement> | null;
  target: React.RefObject<HTMLElement>;
  onIntersect: () => void;
  threshold: number;
  rootMargin: string;
  enabled: boolean;
}) {
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          return entry.isIntersecting && onIntersect();
        }),
      {
        root: root?.current,
        rootMargin,
        threshold,
      },
    );

    const el = target?.current;

    if (!el) {
      return;
    }

    observer.observe(el);

    return () => {
      observer.unobserve(el);
    };
  }, [target.current, onIntersect, root?.current, rootMargin, threshold, enabled]);
}

/**
 * Run an effect once, based on query params.
 */
export function useQueryParamEffect({
  shouldRun,
  run,
}: {
  /**
   * Receives query params, must return boolean to indicate
   * whether effect should run.
   */
  shouldRun: (queryParams: QueryParams) => boolean;

  /**
   * Function to run once `shouldRun` returns `true`.
   */
  run: (queryParams: QueryParams) => void;
}) {
  const queryParams = useQueryParams();

  const effectRan = useRef(false);

  useEffect(() => {
    // Ensure an effect on a query param runs only once
    if (effectRan.current) {
      return;
    }

    if (shouldRun(queryParams)) {
      run(queryParams);

      effectRan.current = true;
    }
  });
}

export const SEARCH_DEBOUNCE_DELAY = 500;

export function useDebounce<T = unknown>(value: T, delay: number) {
  // State and setters for debounced value
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(
    () => {
      // Update debounced value after delay
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);

      // Cancel the timeout if value changes (also on delay change or unmount)
      // This is how we prevent debounced value from updating if value is changed ...
      // .. within the delay period. Timeout gets cleared and restarted.
      return () => {
        clearTimeout(handler);
      };
    },
    [value, delay], // Only re-call effect if value or delay changes
  );

  return debouncedValue;
}

export const useHover = (): { isHovered: boolean; onMouseOver: () => void; onMouseLeave: () => void } => {
  const [isHovered, setIsHovered] = useState(false);

  const onMouseOver = useCallback(() => {
    setIsHovered(true);
  }, []);

  const onMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  return { onMouseOver, onMouseLeave, isHovered };
};

export function useEffectWithPrevDeps<const TD = unknown[]>(effect: (prevDeps: TD) => void | (() => void), deps?: TD) {
  const prevDeps = useRef<TD | undefined>();

  useEffect(
    () => {
      const cleanUpFn = effect((prevDeps.current || []) as TD);
      prevDeps.current = deps;

      return cleanUpFn;
    },
    // biome-ignore lint/correctness/useExhaustiveDependencies: custom hook
    deps as unknown as DependencyList,
  );
}
